import type { ActivityType, Entity, Language } from 'types';
import type { ListCampaign } from '../campaign/types';
import type { Sums } from '../common/types';
import type { EmbeddedUser } from 'modules/core/types';
import type { VendorFactor } from 'modules/supplier/factors/types';
import type { Vendor } from 'modules/supplier/vendor/types';
import type { BlockStatus, BlockStatusTemplate } from '../status/types';
import type { Attachment } from 'modules/attachments/types';
import type { Topic } from 'modules/comments/types';
import type { TaskList } from 'modules/tasks/types';

export type PlanFactor = VendorFactor & {
  vendor: Vendor;
  vendor_factor: number | null;
};

export type BudgetGroup = {
  id: number;
  title: string;
  budget: string | null;
  config: {
    locked: boolean;
  };
  readonly modified_at: number;
};

export type PlanStrategyType = 'NAIVE' | 'RTB' | 'OOH';
export type PlanResolution = 'DAY' | 'WEEK' | 'MONTH';

export type PlanConfig = {
  default_plan_strategy: PlanStrategyType;
  block_status_template: BlockStatusTemplate | null;
  available_strategies: PlanStrategyType[];
  enabled: boolean;
  lock: boolean;
  asset_delivery_contact: string;
  // working days
  asset_delivery_deadline: number;
};

export type PlanningConfig = Record<ActivityType, PlanConfig>;

export type ChangeScope = 'PLAN' | 'PRICES_UPDATE' | 'PRICES' | 'BRIEF_UPDATE' | 'REJECTED_PLAN' | 'REPLAN';

export const blockStates = ['NEW', 'PLANNING', 'SUBMITTED', 'APPROVED', 'DONE', 'CANCELED'] as const;
export type BlockState = (typeof blockStates)[number];

export interface BlockSums extends Sums {
  all_bound: boolean;
}

export interface BlockBase<T extends ActivityType = ActivityType> extends Entity {
  type: T;
  state: BlockState;
  readonly status: BlockStatus;
  title: string;
  client_po_number: string | null;
  readonly final_client_po_number: string | null;
  date_from: string;
  date_to: string;
  deadline: string | null;
  code: string;
  sums: BlockSums;
}

export interface ListBlock extends BlockBase {
  campaign: ListCampaign;
  planners: EmbeddedUser[];
}

type CretiveFields = {
  first_sketch: string;
  materials: { kind: string; amount: number }[];
  styleguide: string;
  copywrite: string;
  website: string;
  social_media: string;
};

export interface BlockDetails<T extends ActivityType = ActivityType> extends BlockBase<T> {
  readonly available_statuses: BlockStatus[];
  readonly etag: string;
  role: {
    manage: boolean;
    plan: boolean;
  };
  // brief fields
  planners: EmbeddedUser[];
  deadline: string;
  budget: string;
  canceled_message: string | null;
  suggested_media: string;
  comment: string;
  // @TODO
  fields: any | CretiveFields;
  languages: Language[];
  brief_attachments: Attachment[];
  target_groups: string;
  target_group_size: string | null;

  topic: Topic;
  task_list: TaskList;
  // mobve theese to block? to
  // version
  readonly issued_at: string;
  readonly version: number;
  readonly scope: ChangeScope;
  readonly motivation: string;
  readonly brief_updated: boolean;
}
